import React, { useMemo } from 'react';
import { Box, Chip, Divider, Stack, Tooltip, Typography } from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import { IReceipt, ITransaction, PaymentReceivedRelationTypeEnum } from 'features/salesOrder/types';
import { getCardStampLabel } from 'features/salesOrder/components/ClientPaymentsList/ClientPaymentsFilters';
import { PLATFORM_LABEL, formatAmount } from 'features/invoices/uiHelpers';
import { useSaleOrderInvoicing } from 'features/invoices/components/SaleOrderInvoices/InvoicingContext';
import { useCashNotes } from 'features/cashNotes';
import SectionCard from './SectionCard';
import { CashNote } from './CashNote';

/**
 * Card de Cobros del home de la orden. Usa los recibos completos del
 * SaleOrderInvoicingProvider (tarjeta, aerolínea, banco, auth, obs) y marca
 * por fila si el cobro ya está facturado. Las transacciones sin recibo
 * (órdenes anteriores al módulo de recibos) se muestran con el mismo formato
 * de lista, así el componente se ve igual en todas las órdenes.
 */

const RELATION_LABEL: Record<PaymentReceivedRelationTypeEnum, string> = {
	[PaymentReceivedRelationTypeEnum.MVOrderTransaction]: 'Pago de orden',
	[PaymentReceivedRelationTypeEnum.VNOrderTransaction]: 'Pago de orden (VN)',
	[PaymentReceivedRelationTypeEnum.CashPayment]: 'Efectivo',
	[PaymentReceivedRelationTypeEnum.WireTransferPayment]: 'Transferencia',
	[PaymentReceivedRelationTypeEnum.MVHotelReserve]: 'Reserva hotel',
	[PaymentReceivedRelationTypeEnum.VNFlightReserve]: 'Reserva vuelo (VN)',
	[PaymentReceivedRelationTypeEnum.VNHotelReserveMV]: 'Reserva hotel (VN)',
};

const formatShortDate = (isoDateTime: string): string => {
	const [year, month, day] = isoDateTime.slice(0, 10).split('-');
	return `${day}/${month}/${year}`;
};

/** Línea de trazabilidad del cobro: tarjeta, aerolínea, banco, auth, ref, obs. */
const receiptDetail = (receipt: IReceipt): string => {
	const parts: string[] = [];

	if (receipt.extCardStamp || receipt.extCardLast4) {
		const stamp = receipt.extCardStamp ? getCardStampLabel(receipt.extCardStamp) : 'Tarjeta';
		parts.push(`${stamp}${receipt.extCardLast4 ? ` ****${receipt.extCardLast4}` : ''}`);
	}
	if (receipt.extAirlineName) parts.push(receipt.extAirlineName);
	if (receipt.extBankName) {
		parts.push(`${receipt.extBankName}${receipt.extBankAccount ? ` ${receipt.extBankAccount}` : ''}`);
	}
	if (receipt.extBranchName) parts.push(receipt.extBranchName);
	if (receipt.extCashSourceType) parts.push(receipt.extCashSourceType);

	const auth = receipt.extAuthCode ?? receipt.authorizationId;
	if (auth) parts.push(`Auth ${auth}`);
	if (receipt.extPaymentReference) parts.push(`Ref ${receipt.extPaymentReference}`);
	if (receipt.observations) parts.push(receipt.observations);

	return parts.join(' · ');
};

/**
 * Id de la transacción cash, cuando el cobro es en efectivo.
 *
 * Es la clave con la que la Support API va a buscar la nota que se escribió al
 * cargar el cobro: `transactionId` acá es el `id` de `cashtransaction`. Para
 * cualquier otro tipo de cobro no hay nota que buscar.
 */
const cashTransactionIdDe = (r: {
	relationType: PaymentReceivedRelationTypeEnum;
	transactionId?: string;
}): number | undefined => {
	if (r.relationType !== PaymentReceivedRelationTypeEnum.CashPayment) return undefined;
	const id = Number(r.transactionId);
	return Number.isInteger(id) && id > 0 ? id : undefined;
};

/** Fila unificada de la lista de cobros (nace de un recibo o de una transacción). */
interface CobroRow {
	key: string;
	createdAt: string;
	amountUsd: number;
	relationType: PaymentReceivedRelationTypeEnum;
	platformType: IReceipt['platformType'];
	detail: string;
	/** undefined = todavía no se sabe (receipts de facturación cargando). */
	invoiced?: boolean;
	/** Cobro sin recibo generado (orden anterior al módulo de recibos). */
	withoutReceipt?: boolean;
	/** Id de la transacción cash, para cruzar con su nota. */
	cashTransactionId?: number;
}

interface CobrosCardProps {
	transactions: ITransaction[];
	loading: boolean;
	error: string | null;
	/** Tipografía compacta (layout mobile/tablet). Se mantiene por compatibilidad. */
	dense?: boolean;
	/** Acción del título (ej. botón + para nuevo cobro). */
	action?: React.ReactNode;
	/** Nodos extra (ej. el Menu del botón +, que se renderiza por portal). */
	children?: React.ReactNode;
}

export const CobrosCard = ({ transactions, loading, error, action, children }: CobrosCardProps): JSX.Element => {
	const { receipts, receiptsLoaded, orderReceipts, orderReceiptsLoaded } = useSaleOrderInvoicing();

	/** IReceipt no trae invoiceId; se cruza por id con los receipts de facturación. */
	const invoicedByReceiptId = useMemo(() => {
		const map: Record<string, boolean> = {};
		for (const receipt of receipts) {
			map[receipt.id] = Boolean(receipt.invoiceId);
		}
		return map;
	}, [receipts]);

	const receiptsSummary = useMemo(() => {
		const linked = receipts.filter((r) => r.invoiceId);
		const free = receipts.filter((r) => !r.invoiceId);
		const sum = (list: typeof receipts): number =>
			Number(list.reduce((acc, r) => acc + (r.amountInMainCurrency ?? 0), 0).toFixed(2));

		return {
			linkedCount: linked.length,
			linkedAmount: sum(linked),
			freeCount: free.length,
			freeAmount: sum(free),
		};
	}, [receipts]);

	/**
	 * Lista unificada: recibos ricos + transacciones sin recibo (cruce por
	 * transactionId). Si los recibos aún no cargaron, se listan las
	 * transacciones directamente con el mismo formato — nunca dos layouts.
	 */
	const rows = useMemo((): CobroRow[] => {
		const transactionToRow = (transaction: ITransaction, withoutReceipt: boolean): CobroRow => ({
			key: `t-${transaction.transactionId}`,
			createdAt: transaction.createdAt,
			amountUsd: Number(transaction.amountInMainCurrency) || 0,
			relationType: transaction.relationType,
			platformType: transaction.platformType,
			detail: transaction.observations ?? '',
			withoutReceipt,
			cashTransactionId: cashTransactionIdDe(transaction),
		});

		if (!orderReceiptsLoaded) {
			return transactions.map((t) => transactionToRow(t, false));
		}

		const receiptRows = orderReceipts.map(
			(receipt): CobroRow => ({
				key: `r-${receipt.id}`,
				createdAt: receipt.createdAt,
				amountUsd: receipt.amountInMainCurrency,
				relationType: receipt.relationType,
				platformType: receipt.platformType,
				detail: receiptDetail(receipt),
				invoiced: receiptsLoaded ? invoicedByReceiptId[receipt.id] ?? false : undefined,
				cashTransactionId: cashTransactionIdDe(receipt),
			}),
		);

		const receiptTransactionIds = new Set(orderReceipts.map((r) => r.transactionId));
		const leftoverRows = transactions
			.filter((t) => !receiptTransactionIds.has(t.transactionId))
			.map((t) => transactionToRow(t, true));

		return [...receiptRows, ...leftoverRows];
	}, [orderReceiptsLoaded, orderReceipts, transactions, receiptsLoaded, invoicedByReceiptId]);

	/**
	 * Notas de los cobros cash. Si la orden no tiene ninguno —la mitad de las
	 * órdenes— la lista queda vacía y el hook no sale a pedir nada.
	 */
	const cashTransactionIds = useMemo(
		() => rows.map((r) => r.cashTransactionId).filter((id): id is number => id !== undefined),
		[rows],
	);
	const { notes: cashNotes, failed: cashNotesFailed } = useCashNotes(cashTransactionIds);

	return (
		<SectionCard title="Cobros" icon={<PaymentsIcon fontSize="small" />} action={action}>
			{children}

			{receiptsLoaded && (receiptsSummary.linkedCount > 0 || receiptsSummary.freeCount > 0) && (
				<Box display="flex" gap={1} flexWrap="wrap" mb={0.5}>
					{receiptsSummary.linkedCount > 0 && (
						<Tooltip title="Cobros ya vinculados a una factura">
							<Chip
								size="small"
								color="success"
								variant="outlined"
								label={`${receiptsSummary.linkedCount} facturado${
									receiptsSummary.linkedCount === 1 ? '' : 's'
								} · ${formatAmount(receiptsSummary.linkedAmount, 'USD')}`}
							/>
						</Tooltip>
					)}
					{receiptsSummary.freeCount > 0 && (
						<Tooltip title="Cobros sin vincular a ninguna factura todavía">
							<Chip
								size="small"
								color="warning"
								label={`${receiptsSummary.freeCount} sin facturar · ${formatAmount(receiptsSummary.freeAmount, 'USD')}`}
							/>
						</Tooltip>
					)}
				</Box>
			)}

			{rows.length > 0 ? (
				<Stack divider={<Divider flexItem />} spacing={1}>
					{rows.map((row) => (
						<Box key={row.key} sx={{ py: 0.5 }}>
							<Stack direction="row" alignItems="center" spacing={1}>
								<Typography variant="body2" color="text.secondary">
									{formatShortDate(row.createdAt)}
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 700 }}>
									{formatAmount(row.amountUsd, 'USD')}
								</Typography>
								<Box sx={{ flex: 1 }} />
								{row.withoutReceipt ? (
									<Tooltip title="Cobro sin recibo generado (orden anterior al módulo de recibos)">
										<Chip size="small" variant="outlined" label="Sin recibo" sx={{ height: 18, fontSize: '0.65rem' }} />
									</Tooltip>
								) : row.invoiced !== undefined ? (
									<Chip
										size="small"
										variant="outlined"
										color={row.invoiced ? 'success' : 'warning'}
										label={row.invoiced ? 'Facturado' : 'Sin facturar'}
										sx={{ height: 18, fontSize: '0.65rem' }}
									/>
								) : null}
							</Stack>

							<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
								{RELATION_LABEL[row.relationType] ?? row.relationType} ·{' '}
								{PLATFORM_LABEL[row.platformType] ?? row.platformType}
							</Typography>

							{row.detail && (
								<Typography variant="caption" sx={{ display: 'block' }}>
									{row.detail}
								</Typography>
							)}

							{row.cashTransactionId !== undefined && cashNotes[row.cashTransactionId] && (
								<CashNote texto={cashNotes[row.cashTransactionId]} />
							)}

							{row.cashTransactionId !== undefined && cashNotesFailed && (
								<Typography variant="caption" sx={{ display: 'block', mt: 0.25 }} color="text.disabled">
									Notas del cobro: no se pudieron cargar
								</Typography>
							)}
						</Box>
					))}
				</Stack>
			) : loading || !orderReceiptsLoaded ? (
				<Typography variant="body2" color="text.secondary">
					Cargando cobros...
				</Typography>
			) : error ? (
				<Typography variant="body2" color="error">
					{error}
				</Typography>
			) : (
				<Typography variant="body2" color="text.secondary">
					La orden todavía no tiene cobros.
				</Typography>
			)}
		</SectionCard>
	);
};

export default CobrosCard;
